// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    ISankeyLinkInput,
    ISankeyNodeInput,
    PILL_TOP_OFFSET_Y,
    SankeyLayoutNode,
    useSankeyLayout,
} from 'admin/failoverDomain/page/components/SummaryCards/useSankeyLayout';
import {
    SankeyHeader,
    SankeyHeaderTitle,
    SankeyTooltip,
} from 'admin/failoverDomain/page/components/SummaryCards/Styles';
import { SankeyNode } from 'admin/failoverDomain/page/components/SummaryCards/SankeyNode';
import { SankeyLink } from 'admin/failoverDomain/page/components/SummaryCards/SankeyLink';

export type { ISankeyNodeInput, ISankeyLinkInput };

const VIEWBOX_WIDTH = 786;
const VIEWBOX_HEIGHT = 280;

export interface ISankeyDiagramProps {
    nodes: ISankeyNodeInput[];
    links: ISankeyLinkInput[];
    leftTitle: string;
    rightTitle: string;
    formatValue?: (v: number) => string;
}

interface ITooltipState {
    x: number;
    y: number;
    text: string;
}

export const SankeyDiagram: React.FC<ISankeyDiagramProps> = ({
    nodes,
    links,
    leftTitle,
    rightTitle,
    formatValue,
}) => {
    const [tooltip, setTooltip] = React.useState<ITooltipState | null>(null);
    const hideTooltip = React.useCallback(() => setTooltip(null), []);
    const showTooltip = React.useCallback(
        ({ x, y }: { x: number; y: number }, text: string) => setTooltip({ x, y, text }),
        []
    );

    const { graph, dividerX: rawDividerX } = useSankeyLayout(nodes, links);
    const dividerX = rawDividerX ?? VIEWBOX_WIDTH / 2;

    if (!graph) {
        return (
            <>
                <SankeyHeader>
                    <SankeyHeaderTitle>{leftTitle}</SankeyHeaderTitle>
                    <SankeyHeaderTitle>{rightTitle}</SankeyHeaderTitle>
                </SankeyHeader>
                <svg width="100%" viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`} aria-hidden="true">
                    <text x={VIEWBOX_WIDTH / 2} y={VIEWBOX_HEIGHT / 2} textAnchor="middle" fontSize={12} fill="#999">
                        No data
                    </text>
                </svg>
            </>
        );
    }

    return (
        <>
            <SankeyHeader>
                <SankeyHeaderTitle>{leftTitle}</SankeyHeaderTitle>
                <SankeyHeaderTitle>{rightTitle}</SankeyHeaderTitle>
            </SankeyHeader>
            <svg
                width="100%"
                viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
                aria-hidden="true"
                onMouseLeave={hideTooltip}
            >
                {/* Subtle vertical divider between HA-status column and online/offline column */}
                <line
                    x1={dividerX} y1={PILL_TOP_OFFSET_Y - 6}
                    x2={dividerX} y2={VIEWBOX_HEIGHT}
                    stroke="#d8d8d8"
                    strokeWidth={0.5}
                />

                {graph.links.map((link) => {
                    const source = link.source as SankeyLayoutNode & { id: string };
                    const target = link.target as SankeyLayoutNode & { id: string };
                    return (
                        <SankeyLink
                            key={`${source.id}->${target.id}`}
                            link={link}
                            formatValue={formatValue}
                            onTooltip={showTooltip}
                            onHideTooltip={hideTooltip}
                        />
                    );
                })}

                {graph.nodes.map((node) => (
                    <g key={(node as SankeyLayoutNode & { id: string }).id}>
                        <SankeyNode node={node} />
                    </g>
                ))}
            </svg>

            {tooltip && (
                <SankeyTooltip style={{ left: tooltip.x + 14, top: tooltip.y - 10 }}>
                    {tooltip.text}
                </SankeyTooltip>
            )}
        </>
    );
};

export default SankeyDiagram;
